
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2, Upload, Loader2, Settings, Users } from 'lucide-react';

interface Service {
  id: string;
  title: string;
  description: string;
  detailed_description?: string;
  image_url?: string;
  service_type: 'energy_healing' | 'therapy' | 'consulting' | 'divination' | 'wellness';
  service_status: 'active' | 'coming_soon' | 'inactive';
  sort_order: number;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

interface HealingStage {
  id: number;
  stage_number: number;
  title: string;
  subtitle?: string;
  description?: string;
  target_description?: string;
  color_theme: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface StageAssignment {
  id: string;
  stage_id: number;
  service_id: string;
  sort_order: number;
  created_at: string;
}

const AdminCertifications = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [stages, setStages] = useState<HealingStage[]>([]);
  const [assignments, setAssignments] = useState<StageAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [isStageDialogOpen, setIsStageDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingStage, setEditingStage] = useState<HealingStage | null>(null);
  const [activeTab, setActiveTab] = useState('services');
  const [serviceFormData, setServiceFormData] = useState({
    title: '',
    description: '',
    detailed_description: '',
    image_url: '',
    service_type: 'energy_healing' as const,
    service_status: 'coming_soon' as const,
    sort_order: 0,
    is_featured: false,
  });
  const [stageFormData, setStageFormData] = useState({
    stage_number: 0,
    title: '',
    subtitle: '',
    description: '',
    target_description: '',
    color_theme: 'blue',
    is_active: true,
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch services
      const { data: servicesData, error: servicesError } = await supabase
        .from('services_master')
        .select('*')
        .order('sort_order', { ascending: true });

      if (servicesError) throw servicesError;

      // Fetch stages
      const { data: stagesData, error: stagesError } = await supabase
        .from('healing_stages')
        .select('*')
        .order('stage_number', { ascending: true });

      if (stagesError) throw stagesError;

      // Fetch assignments
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('stage_service_assignments')
        .select('*')
        .order('sort_order', { ascending: true });

      if (assignmentsError) throw assignmentsError;

      setServices((servicesData || []) as Service[]);
      setStages(stagesData || []);
      setAssignments(assignmentsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load services and stages',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `services/${Date.now()}-${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('certification-images')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('certification-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleServiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      let imageUrl = serviceFormData.image_url;
      
      if (imageFile) {
        const uploadedUrl = await uploadImage(imageFile);
        if (uploadedUrl) {
          imageUrl = uploadedUrl;
        }
      }

      const serviceData = {
        ...serviceFormData,
        image_url: imageUrl,
      };

      if (editingService) {
        const { error } = await supabase
          .from('services_master')
          .update(serviceData)
          .eq('id', editingService.id);

        if (error) throw error;
        
        toast({
          title: 'Success',
          description: 'Service updated successfully',
        });
      } else {
        const { error } = await supabase
          .from('services_master')
          .insert([serviceData]);

        if (error) throw error;
        
        toast({
          title: 'Success',
          description: 'Service created successfully',
        });
      }

      resetServiceForm();
      setIsServiceDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving service:', error);
      toast({
        title: 'Error',
        description: 'Failed to save service',
        variant: 'destructive',
      });
    }
  };

  const handleStageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingStage) {
        const { error } = await supabase
          .from('healing_stages')
          .update(stageFormData)
          .eq('id', editingStage.id);

        if (error) throw error;
        
        toast({
          title: 'Success',
          description: 'Stage updated successfully',
        });
      } else {
        const { error } = await supabase
          .from('healing_stages')
          .insert([{ ...stageFormData, id: stageFormData.stage_number }]);

        if (error) throw error;
        
        toast({
          title: 'Success',
          description: 'Stage created successfully',
        });
      }

      resetStageForm();
      setIsStageDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving stage:', error);
      toast({
        title: 'Error',
        description: 'Failed to save stage',
        variant: 'destructive',
      });
    }
  };

  const handleServiceEdit = (service: Service) => {
    setEditingService(service);
    setServiceFormData({
      title: service.title,
      description: service.description,
      detailed_description: service.detailed_description || '',
      image_url: service.image_url || '',
      service_type: service.service_type as typeof serviceFormData.service_type,
      service_status: service.service_status as typeof serviceFormData.service_status,
      sort_order: service.sort_order,
      is_featured: service.is_featured,
    });
    setIsServiceDialogOpen(true);
  };

  const handleStageEdit = (stage: HealingStage) => {
    setEditingStage(stage);
    setStageFormData({
      stage_number: stage.stage_number,
      title: stage.title,
      subtitle: stage.subtitle || '',
      description: stage.description || '',
      target_description: stage.target_description || '',
      color_theme: stage.color_theme,
      is_active: stage.is_active,
    });
    setIsStageDialogOpen(true);
  };

  const handleServiceDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this service?')) return;

    try {
      const { error } = await supabase
        .from('services_master')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Service deleted successfully',
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete service',
        variant: 'destructive',
      });
    }
  };

  const handleStageDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this stage?')) return;

    try {
      const { error } = await supabase
        .from('healing_stages')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Stage deleted successfully',
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting stage:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete stage',
        variant: 'destructive',
      });
    }
  };

  const resetServiceForm = () => {
    setServiceFormData({
      title: '',
      description: '',
      detailed_description: '',
      image_url: '',
      service_type: 'energy_healing',
      service_status: 'coming_soon',
      sort_order: 0,
      is_featured: false,
    });
    setEditingService(null);
    setImageFile(null);
  };

  const resetStageForm = () => {
    setStageFormData({
      stage_number: 0,
      title: '',
      subtitle: '',
      description: '',
      target_description: '',
      color_theme: 'blue',
      is_active: true,
    });
    setEditingStage(null);
  };

  const handleServiceDialogOpenChange = (open: boolean) => {
    setIsServiceDialogOpen(open);
    if (!open) {
      resetServiceForm();
    }
  };

  const handleStageDialogOpenChange = (open: boolean) => {
    setIsStageDialogOpen(open);
    if (!open) {
      resetStageForm();
    }
  };

  const toggleStageServiceAssignment = async (stageId: number, serviceId: string) => {
    try {
      const existingAssignment = assignments.find(
        a => a.stage_id === stageId && a.service_id === serviceId
      );

      if (existingAssignment) {
        // Remove assignment
        const { error } = await supabase
          .from('stage_service_assignments')
          .delete()
          .eq('id', existingAssignment.id);

        if (error) throw error;
      } else {
        // Add assignment
        const { error } = await supabase
          .from('stage_service_assignments')
          .insert([{
            stage_id: stageId,
            service_id: serviceId,
            sort_order: 0
          }]);

        if (error) throw error;
      }

      fetchData();
      toast({
        title: 'Success',
        description: 'Stage assignment updated successfully',
      });
    } catch (error) {
      console.error('Error updating stage assignment:', error);
      toast({
        title: 'Error',
        description: 'Failed to update stage assignment',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading services and stages...
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Healing Journey Management</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="stages">Stages</TabsTrigger>
          <TabsTrigger value="assignments">Stage Assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Services Management</h2>
            <Dialog open={isServiceDialogOpen} onOpenChange={handleServiceDialogOpenChange}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingService ? 'Edit Service' : 'Add New Service'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleServiceSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Title</Label>
                      <Input
                        id="title"
                        value={serviceFormData.title}
                        onChange={(e) => setServiceFormData({ ...serviceFormData, title: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="service_type">Service Type</Label>
                      <Select 
                        value={serviceFormData.service_type} 
                        onValueChange={(value: typeof serviceFormData.service_type) => setServiceFormData({ ...serviceFormData, service_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="energy_healing">Energy Healing</SelectItem>
                          <SelectItem value="therapy">Therapy</SelectItem>
                          <SelectItem value="consulting">Consulting</SelectItem>
                          <SelectItem value="divination">Divination</SelectItem>
                          <SelectItem value="wellness">Wellness</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="description">Short Description</Label>
                    <Textarea
                      id="description"
                      value={serviceFormData.description}
                      onChange={(e) => setServiceFormData({ ...serviceFormData, description: e.target.value })}
                      rows={2}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="detailed_description">Detailed Description</Label>
                    <Textarea
                      id="detailed_description"
                      value={serviceFormData.detailed_description}
                      onChange={(e) => setServiceFormData({ ...serviceFormData, detailed_description: e.target.value })}
                      rows={4}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="service_status">Status</Label>
                      <Select 
                        value={serviceFormData.service_status} 
                        onValueChange={(value: typeof serviceFormData.service_status) => setServiceFormData({ ...serviceFormData, service_status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="coming_soon">Coming Soon</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="sort_order">Sort Order</Label>
                      <Input
                        id="sort_order"
                        type="number"
                        value={serviceFormData.sort_order}
                        onChange={(e) => setServiceFormData({ ...serviceFormData, sort_order: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="image">Image</Label>
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    />
                    {serviceFormData.image_url && (
                      <div className="mt-2">
                        <img
                          src={serviceFormData.image_url}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded"
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_featured"
                      checked={serviceFormData.is_featured}
                      onCheckedChange={(checked) => setServiceFormData({ ...serviceFormData, is_featured: checked })}
                    />
                    <Label htmlFor="is_featured">Featured Service</Label>
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleServiceDialogOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={uploading}>
                      {uploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        editingService ? 'Update' : 'Create'
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Image</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {services.map((service) => (
                    <TableRow key={service.id}>
                      <TableCell>
                        {service.image_url && (
                          <img
                            src={service.image_url}
                            alt={service.title}
                            className="w-12 h-12 object-cover rounded"
                          />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{service.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {service.service_type.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            service.service_status === 'active' ? 'default' : 
                            service.service_status === 'coming_soon' ? 'secondary' : 'destructive'
                          }
                        >
                          {service.service_status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {service.is_featured && <Badge>Featured</Badge>}
                      </TableCell>
                      <TableCell>{service.sort_order}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleServiceEdit(service)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleServiceDelete(service.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stages" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Healing Stages Management</h2>
            <Dialog open={isStageDialogOpen} onOpenChange={handleStageDialogOpenChange}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stage
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingStage ? 'Edit Stage' : 'Add New Stage'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleStageSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="stage_number">Stage Number</Label>
                      <Input
                        id="stage_number"
                        type="number"
                        min="0"
                        max="4"
                        value={stageFormData.stage_number}
                        onChange={(e) => setStageFormData({ ...stageFormData, stage_number: parseInt(e.target.value) || 0 })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="color_theme">Color Theme</Label>
                      <Select 
                        value={stageFormData.color_theme} 
                        onValueChange={(value) => setStageFormData({ ...stageFormData, color_theme: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="red">Red</SelectItem>
                          <SelectItem value="orange">Orange</SelectItem>
                          <SelectItem value="yellow">Yellow</SelectItem>
                          <SelectItem value="green">Green</SelectItem>
                          <SelectItem value="blue">Blue</SelectItem>
                          <SelectItem value="purple">Purple</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="stage_title">Title</Label>
                    <Input
                      id="stage_title"
                      value={stageFormData.title}
                      onChange={(e) => setStageFormData({ ...stageFormData, title: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="subtitle">Subtitle</Label>
                    <Input
                      id="subtitle"
                      value={stageFormData.subtitle}
                      onChange={(e) => setStageFormData({ ...stageFormData, subtitle: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="stage_description">Description</Label>
                    <Textarea
                      id="stage_description"
                      value={stageFormData.description}
                      onChange={(e) => setStageFormData({ ...stageFormData, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="target_description">Target Audience</Label>
                    <Textarea
                      id="target_description"
                      value={stageFormData.target_description}
                      onChange={(e) => setStageFormData({ ...stageFormData, target_description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="stage_is_active"
                      checked={stageFormData.is_active}
                      onCheckedChange={(checked) => setStageFormData({ ...stageFormData, is_active: checked })}
                    />
                    <Label htmlFor="stage_is_active">Active</Label>
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleStageDialogOpenChange(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit">
                      {editingStage ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage #</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Subtitle</TableHead>
                    <TableHead>Color</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stages.map((stage) => (
                    <TableRow key={stage.id}>
                      <TableCell className="font-medium">{stage.stage_number}</TableCell>
                      <TableCell>{stage.title}</TableCell>
                      <TableCell>{stage.subtitle}</TableCell>
                      <TableCell>
                        <div className={`w-4 h-4 rounded-full bg-${stage.color_theme}-500`}></div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={stage.is_active ? 'default' : 'secondary'}>
                          {stage.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStageEdit(stage)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStageDelete(stage.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assignments" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Stage Service Assignments</h2>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Assign Services to Stages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {stages.map((stage) => (
                  <div key={stage.id} className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-3">
                      Stage {stage.stage_number}: {stage.title}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {services.map((service) => {
                        const isAssigned = assignments.some(
                          a => a.stage_id === stage.stage_number && a.service_id === service.id
                        );
                        return (
                          <div
                            key={service.id}
                            className={`p-3 border rounded cursor-pointer transition-colors ${
                              isAssigned ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                            }`}
                            onClick={() => toggleStageServiceAssignment(stage.stage_number, service.id)}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">{service.title}</span>
                              <Badge variant={service.service_status === 'active' ? 'default' : 'secondary'}>
                                {service.service_status}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminCertifications;
